// Who may do what, at a glance (pages/ux-full components/setup.js PermissionGrid): the few things an
// owner asks about, each drawn from the permissions behind it, for every role. Blueprint draws this
// grid first; every permission is still one tap away in the full matrix, which is where a role changes.
import type { MatrixPermission } from './settings-model.ts';

export interface GridRow {
  what: string;
  /** The permissions behind it: a role may do it when it holds every one. */
  keys: readonly string[];
}

export const GRID: readonly { group: string; rows: readonly GridRow[] }[] = [
  {
    group: 'Schedule',
    rows: [
      { what: 'Book, move and cancel visits', keys: ['jobs.book', 'jobs.cancel'] },
      { what: 'Send a visit to a technician', keys: ['jobs.dispatch'] },
    ],
  },
  {
    group: 'Customers',
    rows: [
      { what: 'See and change customers', keys: ['customers.view', 'customers.edit'] },
      { what: 'See gate and lockbox codes', keys: ['customers.pii.view'] },
    ],
  },
  {
    group: 'Jobs and estimates',
    rows: [
      { what: 'Build options and price them', keys: ['estimates.build'] },
      { what: 'OK a price below the floor', keys: ['estimates.approve'] },
    ],
  },
  {
    group: 'Money',
    rows: [
      { what: 'Take a payment', keys: ['payments.record'] },
      { what: 'Refund a payment', keys: ['payments.refund'] },
      { what: 'See costs and margins', keys: ['pricebook.cost.view', 'finance.margins.view'] },
    ],
  },
  {
    group: 'Books and payroll',
    rows: [
      { what: 'Pay bills', keys: ['ap.manage'] },
      { what: 'Close a month', keys: ['accounting.close'] },
      { what: 'See pay and payroll', keys: ['payroll.view'] },
    ],
  },
];

/**
 * yes: the role holds every permission behind the row; some: only part of them; named: none, and the
 * row needs one only the owner gives, through a role of their own beside this one (BL-137); no: none.
 */
export type GridCell = 'yes' | 'some' | 'named' | 'no';

export const CELL_WORDS: Record<GridCell, { mark: string; says: string }> = {
  yes: { mark: '✓', says: 'May' },
  some: { mark: 'Some', says: 'Some of it' },
  named: { mark: 'Owner', says: 'Only with a role the owner gives' },
  no: { mark: '–', says: 'May not' },
};

export function gridCell(
  role: { key: string; permissions: readonly string[] },
  keys: readonly string[],
  perms: readonly MatrixPermission[],
): GridCell {
  const held = keys.filter((k) => role.permissions.includes(k)).length;
  if (held === keys.length) return 'yes';
  if (held > 0) return 'some';
  const ownerOnly = keys.some((k) => perms.find((p) => p.key === k)?.ownerOnly);
  return ownerOnly && role.key !== 'owner' ? 'named' : 'no';
}

/** The grid's groups and rows for this company: a permission it doesn't have leaves its row out. */
export function gridFor(perms: readonly MatrixPermission[]): { group: string; rows: GridRow[] }[] {
  const known = new Set(perms.map((p) => p.key));
  return GRID.map((g) => ({
    group: g.group,
    rows: g.rows
      .map((r) => ({ ...r, keys: r.keys.filter((k) => known.has(k)) }))
      .filter((r) => r.keys.length > 0),
  })).filter((g) => g.rows.length > 0);
}
